import React, { useEffect, useState, useRef } from 'react';
import { Chart as ChartJS, Tooltip, Legend, PointElement, LinearScale } from 'chart.js';
import { Bubble, getElementAtEvent } from 'react-chartjs-2';

import { semAreasPlugin, chartAreaBorderPlugin, htmlLegendPlugin } from '/src/utilities/chart-plugins';
import { colors } from '/src/utilities/formats';
import { saeByIc, preProcess } from '../../utilities/sae-utils';
import { Col, Container, Row } from 'react-bootstrap';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

export default function SAEChart({
    formData = null,
    quadrant = null,
    legend = false,
    institutionData = null,
    y2 = null,
    icWall = false,
    setCounts
}) {

    const [datasets, setData] = useState(null);
    const [rawData, setRawData] = useState(null);
    const [table, setTable] = useState(null);
    const chartRef = useRef();

    if (!y2) {
        formData?.toggle == 'bachelors' ? y2 = 1.5 : y2 = 1.25;
    }
    useEffect(() => {
        async function fetchData() {
            try {
                let shownData, hiddenData, allData, associatesData, bachelorsData, sfData;
                if (!rawData) {
                    const response = await fetch("/wp-content/plugins/ace-carnegie-institution-charts/frontend/static/data/dynamic.json");
                    allData = preProcess(await response.json(), institutionData?.post_meta.unitid.value);
                    setRawData(allData);
                }
                else {
                    allData = rawData;
                }

                // icWall is set on the profile view, and there is no additional functionality. 
                if (icWall) {
                    shownData = isolateData(allData, 'ic2025', institutionData?.post_meta.ic2025.value);
                }
                else {
                    let showTable = false;

                    associatesData = filterByTab(allData, 'associates');
                    bachelorsData = filterByTab(allData, 'bachelors');
                    sfData = filterByTab(allData, 'sf');

                    if (formData?.institution) {
                        associatesData = filterByName(associatesData, formData.institution);
                        bachelorsData = filterByName(bachelorsData, formData.institution);
                        sfData = filterByName(sfData, formData.institution);
                        showTable = true;
                    }

                    if (formData?.stabbr && formData.stabbr !== '') {
                        associatesData = isolateData(associatesData, 'stabbr', formData.stabbr);
                        bachelorsData = isolateData(bachelorsData, 'stabbr', formData.stabbr);
                        sfData = isolateData(sfData, 'stabbr', formData.stabbr);
                        showTable = true;
                    }

                    for (const key in formData) {
                        if (['institution', 'stabbr', 'filter', 'toggle'].indexOf(key) >= 0) continue;
                        associatesData = isolateData(associatesData, key, formData[key]);
                        bachelorsData = isolateData(bachelorsData, key, formData[key]);
                        sfData = isolateData(sfData, key, formData[key]);
                        showTable = true;
                    }

                    switch (formData?.toggle) {
                        case 'bachelors':
                            shownData = bachelorsData;
                            break;
                        case 'associates':
                            shownData = associatesData;
                            break;
                        case 'sf':
                            shownData = sfData;
                            break;
                        default:
                            shownData = bachelorsData;
                    }
                    setCounts({
                        bachelors: bachelorsData.length,
                        associates: associatesData.length,
                        sf: sfData.length,
                    })
                    if (showTable) {
                        setTable(jsonToTable(shownData,
                            [
                                { 'field': 'name', 'displayName': 'Institution' },
                                { 'field': 'stabbr', 'displayName': 'State' },
                                { 'field': 'sae_urm_2023', 'displayName': 'Underrepresented<br />Minorities %' },
                                { 'field': 'pell_2023', 'displayName': 'Pell %' },
                                { 'field': 'access_ratio', 'displayName': 'Access Ratio' },
                                { 'field': 'sae_2025_earnings', 'displayName': '8-Year Median<br />Earnings' },
                                { 'field': 'earnings_ratio', 'displayName': 'Earnings Ratio' }
                            ]
                        ));

                    } else {
                        setTable(null);
                    }
                }

                let aggregate;
                if (formData?.filter) {
                    aggregate = aggregateData(shownData, formData.filter);
                }
                else {
                    aggregate = aggregateData(shownData);
                }
                setData({ datasets: aggregate });
            } catch (err) {
                console.log(err);
            }
        }
        fetchData();
    }, [formData ?? null]);


    const opts = {
        animation: false,
        responsive: true,
        layout: {
            padding: {
                left: 20,
                right: 50,
                top: 20,
                bottom: 20
            }
        },
        scales: {
            x: {
                min: 0,
                max: 3.03,
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: "Access Measure",
                    padding: 20,
                    font: {
                        size: 16
                    }
                }
            },
            y: {
                min: 0,
                max: 3.023,
                grid: {
                    display: false
                },
                title: {
                    display: true,
                    text: "Earnings Measure",
                    padding: 20,
                    font: {
                        size: 16
                    }
                }
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            htmlLegend: {
                // ID of the container to put the legend in
                containerID: 'legendDiv',
            },
            semAreas: {
                x1: 1,
                y1: 1,
                y2: y2,
                shade: quadrant,
                showLabels: true
            },
            tooltip: {
                enabled: false,
                position: 'nearest',
                external: (context) => {
                    // Tooltip Element
                    const { chart, tooltip } = context;

                    const tooltipEl = chart.canvas.parentNode.querySelector('div.tooltip');
                    // Hide if no tooltip
                    if (tooltip.opacity === 0) {
                        tooltipEl.style.opacity = 0;
                        return;
                    }

                    // Set Text
                    if (tooltip.body) {
                        const ds = tooltip.dataPoints[0].raw;
                        const info = `
                        <div><strong>${ds.name}</strong></div>
                        <div>Access: ${ds.access_ratio.toFixed(2)} | Earnings: ${ds.earnings_ratio.toFixed(2)}</div>
                        `;
                        tooltipEl.innerHTML = info;
                    }

                    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

                    // Display, position, and set styles for font
                    tooltipEl.style.opacity = 1;
                    tooltipEl.style.left = positionX + tooltip.caretX + 'px';
                    tooltipEl.style.top = positionY + tooltip.caretY + 'px';
                    tooltipEl.style.width = "auto";
                    tooltipEl.style.position = 'absolute';

                }
            },
            chartAreaBorder: {
                borderColor: '#CECECE',
                borderWidth: 2,
            }
        },

    }

    const onClick = (event) => {
        let elements = getElementAtEvent(chartRef.current, event);
        if (!icWall && elements.length > 0) {

            const datasetIndex = elements[0].datasetIndex;
            const dataIndex = elements[0].index;

            setTable(jsonToTable([datasets.datasets[datasetIndex]['data'][dataIndex]],
                [
                    { 'field': 'name', 'displayName': 'Institution' },
                    { 'field': 'sae_urm_2023', 'displayName': 'Underrepresented<br />Minorities %' },
                    { 'field': 'pell_2023', 'displayName': 'Pell %' },
                    { 'field': 'access_ratio', 'displayName': 'Access Ratio' },
                    { 'field': 'sae_2025_earnings', 'displayName': '8-Year Median<br />Earnings' },
                    { 'field': 'earnings_ratio', 'displayName': 'Earnings Ratio' }
                ]
            ));


        }
    }

    if (!datasets) return null;

    let plugins = [
        semAreasPlugin,
        chartAreaBorderPlugin
    ];
    if (legend) {
        plugins.push(htmlLegendPlugin);
    }
    return (
        <Container>
            <Row>
                <Col>
                    <p className="d-block d-md-none small">Note: Scroll to the right to view the entire chart.</p>
                    <div className='bubble-chart-outer'>
                        <div className='bubble-chart'>
                            <Bubble
                                ref={chartRef}
                                data={datasets}
                                options={opts}
                                plugins={plugins}
                                onClick={onClick}
                                updateMode="show"
                            />
                            <div className="tooltip">tooltip</div>
                        </div>
                    </div>
                </Col></Row>
            <Row>
                <Col xs={12} md={6} className='pb-3 p-lg-5'>
                    <div><strong>Access Measure</strong><br />Enrollment of Pell Grant recipients and students from underrepresented racial/ethnic groups compared to a similar population in their area.</div>
                </Col>
                <Col xs={12} md={6} className='pb-3 p-lg-5'>
                    <div><strong>Earnings Measure</strong><br />Undergraduate student earnings 8 years post-enrollment compared to a similar population in their area with a high school diploma or higher credential.</div>
                </Col>
            </Row>
            {table && <div dangerouslySetInnerHTML={{ __html: table }}></div>}
        </Container>
    );
}

function aggregateData(data, field, name_filter = null) {
    const datasets = [];
    const colorArray = Object.values(colors);

    let filteredData = data;
    let size = 3;
    if (name_filter) {
        filteredData = filterByName(filteredData, name_filter);
        size = 5;
    }

    // default
    datasets.push({
        label: 'All',
        data: filteredData,
        parsing: {
            xAxisKey: 'x',
            yAxisKey: 'y'
        },
        borderColor: colors.lightblue,
        borderWidth: 1,
        radius: size,
    });
    if (field) {
        const cats = uniqueValues(filteredData, field);
        let i = 0;
        for (let cat of cats) {
            let label = saeByIc.filter((ic) => {
                return ic.ic2025 == cat;
            }
            ).pop();
            let tmp = {};
            tmp.label = label.text ?? "Overall";
            tmp.data = isolateData(filteredData, field, cat);
            tmp.hidden = true;
            tmp.parsing = {
                xAxisKey: 'x',
                yAxisKey: 'y',
            }
            tmp.borderColor = colorArray[i++ % 6];
            tmp.borderWidth = 1;
            tmp.radius = size;
            datasets.push(tmp);
        }
    }
    return datasets;
}

function isolateData(data, field, value) {
    if (typeof value == 'object') {
        const filteredSet = data.filter((row) => {
            return value.indexOf(row[field]) > -1;
        });
        return filteredSet;
    } else if (typeof value == 'string') {
        return data.filter((row) => row[field] == value);
    }
}

function uniqueValues(data, field) {
    return [...new Set(data.map((row) => row[field]))].sort();
}

function filterByName(data, name) {
    return data.filter((row) => row.name.match(new RegExp(`${name}`, 'ig')));
}

function filterByTab(data, tab) {
    const ics = saeByIc.flatMap((ic) => {
        if (ic.tab == tab) return ic.ic2025;
    }).filter(element => element !== undefined);

    return data.filter((row) => ics.indexOf(row.ic2025) > -1);
}
function filterByYAxis(data, yAxis) {
    const ics = saeByIc.flatMap((ic) => {
        if (ic.yLine == yAxis) return ic.ic2025;
    }).filter(element => element !== undefined);

    return data.filter((row) => ics.indexOf(row.ic2025) > -1);
}


function jsonToTable(data, fields) {
    let output;
    if (!data.length) {
        return "<h4>No Institutions Found</h4><p>Please try a different search term.</p>";
    }
    output = `<h4>${data.length} Institutions</h4>`;
    output += "<table class='table table-hover table-archive-institution'>";
    output += "<thead class=''>";
    output += "<tr>";
    for (let field in fields) {
        output += `<th>${fields[field].displayName}</th>`;
    }
    output += "</tr>";
    output += "</thead>";
    output += "<tbody>";

    for (let q = 0; q < data.length; q++) {
        output += "<tr>";
        for (let s = 0; s < fields.length; s++) {
            let field = fields[s].field;
            if (field == 'name') {
                output += `<td><a href='/institution/${data[q]['slug']}'>${data[q]['name']}</td>`;
            }
            else if (['pell_2023', 'sae_urm_2023'].includes(field)) {
                output += `<td>${(data[q][field] * 100).toFixed(2)}%</td>`;
            }
            else if (field == 'sae_2025_earnings') {
                output += `<td>$${data[q][field].toLocaleString('en-US')}</td>`;
            }
            else if (field == 'access_ratio' || field == 'earnings_ratio') {
                output += `<td>${data[q][field].toFixed(2)}</td>`;
            }
            else {
                output += `<td>${data[q][field]}</td>`;
            }
        }
        output += "</tr>";
        // output += `<tr>`;
        // output += `<td colspan=${fields.length}><pre>`;
        // output += JSON.stringify(data[q], null, 2);
        // output += `</pre></td>`;
        // output += `</tr>`;
    }
    output += "</tbody>";
    output += "</table>";
    return output;

}